import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../../store";
import stageService from "../services/stageService";
import { Stage, CreateStageRequest, UpdateStageRequest, StageFilters } from "../types/stage";

interface StageState {
  stages: Stage[];
  loading: boolean;
  error: string | null;
  selectedStage: Stage | null;
  filters: StageFilters;
  totalCount: number;
  currentPage: number;
  pageSize: number;
}

const initialState: StageState = {
  stages: [],
  loading: false,
  error: null,
  selectedStage: null,
  filters: {},
  totalCount: 0,
  currentPage: 1,
  pageSize: 10,
};

// Async thunks
export const fetchStages = createAsyncThunk(
  "stages/fetchStages",
  async (filters: StageFilters = {}) => {
    const response = await stageService.getAllStages(filters);
    console.log("stages=======", response)
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.error || "Failed to fetch stages");
    }
  }
);

export const fetchStageById = createAsyncThunk(
  "stages/fetchStageById",
  async (id: string) => {
    const response = await stageService.getStageById(id);
    if (response.success && response.data) {
      return response.data;
    } else {
      throw new Error(response.error || "Failed to fetch stage");
    }
  }
);

export const createStage = createAsyncThunk(
  "stages/createStage",
  async (stageData: CreateStageRequest) => {
    console.log('createStage thunk called with:', stageData);
    const response = await stageService.createStage(stageData);
    console.log('createStage service response:', response);
    if (response.success && response.data) {
      console.log('Stage created successfully:', response.data);
      return response.data;
    } else {
      const errorMessage = response.error || "Failed to create stage";
      console.error('Stage creation failed:', errorMessage);
      throw new Error(errorMessage);
    }
  }
);

export const updateStage = createAsyncThunk(
  "stages/updateStage",
  async ({ id, stageData }: { id: string; stageData: Partial<CreateStageRequest> }) => {
    const response = await stageService.updateStage(id, stageData);
    if (response.success && response.data) {
      return response.data;
    } else {
      throw new Error(response.error || "Failed to update stage");
    }
  }
);

export const deleteStage = createAsyncThunk(
  "stages/deleteStage",
  async (id: string) => {
    const response = await stageService.deleteStage(id);
    if (response.success) {
      return id;
    } else {
      throw new Error(response.error || "Failed to delete stage");
    }
  }
);

export const fetchStagesTree = createAsyncThunk(
  "stages/fetchStagesTree",
  async () => {
    const response = await stageService.getStagesTree();
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.error || "Failed to fetch stages tree");
    }
  }
);

export const searchStages = createAsyncThunk(
  "stages/searchStages",
  async (searchTerm: string) => {
    const response = await stageService.searchStages(searchTerm);
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.error || "Failed to search stages");
    }
  }
);

const stageSlice = createSlice({
  name: "stages",
  initialState,
  reducers: {
    setSelectedStage: (state, action: PayloadAction<Stage | null>) => {
      state.selectedStage = action.payload;
    },
    clearSelectedStage: (state) => {
      state.selectedStage = null;
    },
    setFilters: (state, action: PayloadAction<StageFilters>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch stages
      .addCase(fetchStages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStages.fulfilled, (state, action) => {
        state.loading = false;
        state.stages = action.payload;
        state.totalCount = action.payload.length;
      })
      .addCase(fetchStages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch stages";
      })
      
      // Fetch stage by ID
      .addCase(fetchStageById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStageById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedStage = action.payload;
      })
      .addCase(fetchStageById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch stage";
      })
      
      // Create stage
      .addCase(createStage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createStage.fulfilled, (state, action) => {
        state.loading = false;
        state.stages.push(action.payload);
        state.totalCount += 1;
      })
      .addCase(createStage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create stage";
      })
      
      // Update stage
      .addCase(updateStage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateStage.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.stages.findIndex(stage => stage.id === action.payload.id);
        if (index !== -1) {
          state.stages[index] = action.payload;
        }
        if (state.selectedStage?.id === action.payload.id) {
          state.selectedStage = action.payload;
        }
      })
      .addCase(updateStage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update stage";
      })
      
      // Delete stage
      .addCase(deleteStage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteStage.fulfilled, (state, action) => {
        state.loading = false;
        state.stages = state.stages.filter(stage => stage.id !== action.payload);
        state.totalCount -= 1;
        if (state.selectedStage?.id === action.payload) {
          state.selectedStage = null;
        }
      })
      .addCase(deleteStage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to delete stage";
      })
      
      // Fetch stages tree
      .addCase(fetchStagesTree.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStagesTree.fulfilled, (state, action) => {
        state.loading = false;
        state.stages = action.payload;
        state.totalCount = action.payload.length;
      })
      .addCase(fetchStagesTree.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch stages tree";
      })
      
      // Search stages
      .addCase(searchStages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchStages.fulfilled, (state, action) => {
        state.loading = false;
        state.stages = action.payload;
        state.totalCount = action.payload.length;
      })
      .addCase(searchStages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to search stages";
      });
  },
});

// Selectors
export const selectStages = (state: RootState) => state.stages.stages;
export const selectStagesLoading = (state: RootState) => state.stages.loading;
export const selectStagesError = (state: RootState) => state.stages.error;
export const selectSelectedStage = (state: RootState) => state.stages.selectedStage;
export const selectStagesFilters = (state: RootState) => state.stages.filters;
export const selectStagesTotalCount = (state: RootState) => state.stages.totalCount;
export const selectStagesCurrentPage = (state: RootState) => state.stages.currentPage;
export const selectStagesPageSize = (state: RootState) => state.stages.pageSize;

// Actions
export const {
  setSelectedStage,
  clearSelectedStage,
  setFilters,
  clearFilters,
  setCurrentPage,
  setPageSize,
  clearError,
} = stageSlice.actions;

export default stageSlice.reducer;
